import { CmykDots } from '@eliadmin/core/src/components/nt/ui/cmyk';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/**
 * Figma text styles FontStyles/Heading/H1–H6 = classes in `src/styles/typography.css`; `title-s` is
 * the unstyled Alan Sans Bold 16 of card and list titles (a `p` unless `as` says otherwise).
 */
const SIZES = {
  h1: 'nt-h1',
  h2: 'nt-h2',
  h3: 'nt-h3',
  h4: 'nt-h4',
  h5: 'nt-h5',
  h6: 'nt-h6',
  'title-s': 'nt-title-s',
} as const;

export type HeadingSize = keyof typeof SIZES;

type HeadingElement = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'div';

/**
 * Heading from the administration. The title is HTML: ` <br>` breaks the line from md, `<span>`
 * colours words in the CMYK order of the brand (1st cyan, 2nd magenta, 3rd yellow) – Figma hero
 * „Tonery a náplne“.
 */
export function Heading({
  title,
  size,
  as,
  id,
  className,
}: {
  title: string;
  size: HeadingSize;
  /** Semantic element; defaults to the size. */
  as?: HeadingElement;
  id?: string;
  className?: string;
}) {
  if (!title) return null;
  const Element = as ?? (size === 'title-s' ? 'p' : size);

  return (
    <Element
      id={id}
      className={cn(
        SIZES[size],
        'nt-break-md text-[#0D110D]',
        '[&_span:nth-of-type(1)]:text-[#00A3E0] [&_span:nth-of-type(2)]:text-[#E5007E] [&_span:nth-of-type(3)]:text-[#FFD200]',
        className,
      )}
      dangerouslySetInnerHTML={{ __html: title }}
    />
  );
}

/**
 * Figma „p.eyebrow“ above section headings („Sprievodca výberom“, „FAQ“): the CMYK dots and the text
 * in Body/M Semibold.
 */
export function Eyebrow({ text, className }: { text: string; className?: string }) {
  if (!text) return null;

  return (
    <p className={cn('nt-body-m-semibold flex items-center gap-3 text-[#0D110D]', className)}>
      <CmykDots />
      <span dangerouslySetInnerHTML={{ __html: text }} />
    </p>
  );
}
